// Component: SessionCoverageChip.jsx
// Purpose: Quiet strategy-session coverage chip with on-demand desktop and phone detail views.

import React from 'react';
import { Badge, Icon, Tooltip } from '../../../components/ds';
import { _istTimeFmt } from '../../../utils/tradingTransforms';

const DETAIL_ID = 'session-coverage-detail';

function formatLastBar(lastBarAt) {
  if (!lastBarAt) return null;
  const parsed = Date.parse(lastBarAt);
  if (!Number.isFinite(parsed)) return null;
  return `${_istTimeFmt.format(new Date(parsed))} IST`;
}

function capitalise(value) {
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function closedReason(coverage) {
  const fromText = coverage?.text?.split('—').pop()?.trim();
  return capitalise(fromText || coverage?.cause || 'no session');
}

function chipSpec(coverage, compact) {
  const totalReasons = (coverage.reasonCounts || []).reduce((sum, item) => sum + item.count, 0);
  switch (coverage.status) {
    case 'covered':
      return {
        variant: 'neutral', mono: true, expandable: true,
        label: `${coverage.evaluatedBars ?? 0} bars`,
        leading: <Icon name="success" size={12} />,
      };
    case 'degraded': {
      const cause = { feed_stale: 'feed stale', stale_evidence: 'stale evidence' }[coverage.cause]
        || String(coverage.cause || 'coverage issue').replaceAll('_', ' ');
      return {
        variant: 'warn', dot: true, expandable: true, status: true,
        label: compact ? 'Degraded' : `Degraded · ${totalReasons || cause}`,
      };
    }
    case 'absent':
      return { variant: 'warn', dot: true, expandable: true, status: true, label: 'No worker' };
    case 'coverage_unknown':
      return {
        variant: 'neutral', expandable: false,
        label: coverage.cause === 'no_evaluated_bars' ? 'No bars yet' : 'Coverage unknown',
      };
    case 'closed':
      return { variant: 'neutral', expandable: false, label: `No session · ${closedReason(coverage)}` };
    default:
      return { variant: 'neutral', expandable: false, label: coverage.text || 'Coverage unknown' };
  }
}

export function SessionCoverageChip({ coverage, expanded = false, onToggle, compact = false }) {
  if (!coverage) return null;
  const spec = chipSpec(coverage, compact);
  const lastBar = formatLastBar(coverage.lastBarAt);
  const tooltip = `${coverage.text}${lastBar ? ` · last bar ${lastBar}` : ''}`;
  const badge = (
    <Badge
      variant={spec.variant}
      size="sm"
      mono={spec.mono}
      dot={spec.dot}
      role={spec.status ? 'status' : undefined}
    >
      {spec.leading}
      <span className="tabular-nums">{spec.label}</span>
      {spec.expandable && !compact && (
        <Icon
          name={expanded ? 'chevron-down' : 'chevron-right'}
          size={12}
          data-testid="coverage-chevron"
        />
      )}
    </Badge>
  );

  if (spec.expandable) {
    return (
      <Tooltip content={tooltip} className="shrink-0">
        <button
          type="button"
          className="shrink-0 min-h-6 appearance-none border-0 bg-transparent p-0 cursor-pointer"
          aria-expanded={compact ? undefined : expanded}
          aria-controls={compact ? undefined : DETAIL_ID}
          onClick={onToggle}
        >
          {badge}
        </button>
      </Tooltip>
    );
  }

  return (
    <Tooltip content={tooltip} className="shrink-0">
      <span className="shrink-0 inline-flex" tabIndex={0}>{badge}</span>
    </Tooltip>
  );
}

function detailModel(coverage) {
  const lastBar = formatLastBar(coverage?.lastBarAt);
  const figures = [
    coverage?.evaluatedBars != null ? `${coverage.evaluatedBars} bars evaluated` : null,
    lastBar ? `last bar ${lastBar}` : null,
  ].filter(Boolean);
  const summary = [coverage?.status === 'absent' ? 'Worker not reporting' : 'This session', ...figures].join(' · ');
  const runner = (coverage?.reasonCounts || []).filter((item) => item.source === 'runner');
  const oms = (coverage?.reasonCounts || []).filter((item) => item.source === 'oms');
  return { summary, figures: figures.join(' · '), runner, oms };
}

function ReasonValues({ items }) {
  return items.map((item, index) => (
    <React.Fragment key={`${item.source}:${item.reason}`}>
      {index > 0 && ' · '}
      <span>{item.label} </span>
      <span className="font-mono tabular-nums text-fg-1">{item.count}</span>
    </React.Fragment>
  ));
}

export function SessionCoverageDetail({ coverage, onClose, layout = 'strip' }) {
  if (!coverage) return null;
  const { summary, figures, runner, oms } = detailModel(coverage);

  if (layout === 'sheet') {
    return (
      <div data-testid="session-coverage-detail" className="rounded-lg bg-bg-2 p-3 text-xs text-fg-2 space-y-2">
        <div className="flex items-start justify-between gap-3">
          <span className="text-fg-3">{coverage.status === 'absent' ? 'Status' : 'This session'}</span>
          <span className="text-right">
            {coverage.status === 'absent' ? summary : (figures || 'No evaluated bars')}
          </span>
        </div>
        {runner.length > 0 && (
          <div className="flex items-start justify-between gap-3">
            <span className="text-fg-3 shrink-0">Runner skipped</span>
            <span className="text-right"><ReasonValues items={runner} /></span>
          </div>
        )}
        {oms.length > 0 && (
          <div className="flex items-start justify-between gap-3">
            <span className="text-fg-3 shrink-0">Entry refused</span>
            <span className="text-right"><ReasonValues items={oms} /></span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      id={DETAIL_ID}
      data-testid="session-coverage-detail"
      className="min-h-8 shrink-0 border-b border-[var(--divider)] bg-white dark:bg-bg-2 px-4 py-1.5 flex items-center gap-3 text-xs text-fg-2"
    >
      <span className="shrink-0">{summary}</span>
      {runner.length > 0 && (
        <>
          <span aria-hidden="true" className="text-fg-3">│</span>
          <span><span className="text-fg-3">Runner skipped: </span><ReasonValues items={runner} /></span>
        </>
      )}
      {oms.length > 0 && (
        <>
          <span aria-hidden="true" className="text-fg-3">│</span>
          <span><span className="text-fg-3">Entry refused: </span><ReasonValues items={oms} /></span>
        </>
      )}
      {onClose && (
        <button
          type="button"
          className="ml-auto shrink-0 btn btn-ghost btn-sm btn-icon"
          aria-label="Close"
          onClick={onClose}
        >
          <Icon name="close" size={14} />
        </button>
      )}
    </div>
  );
}

export default SessionCoverageChip;
